import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// ============================================================================
// QUILL EMBED BLOTS FOR ATOMIC MENTIONS
// Using Embed (not Inline) ensures single-character atomic behavior
// ============================================================================

const Embed = Quill.import('blots/embed');

class MentionBlot extends Embed {
  static blotName = 'mention';
  static tagName = 'span';
  static className = 'ql-mention';

  static create(data) {
    const node = super.create();
    node.setAttribute('data-id', data.id);
    node.setAttribute('data-type', data.type);
    node.setAttribute('data-value', data.value);
    node.setAttribute('contenteditable', 'false');
    node.classList.add('mention');
    node.classList.add(`mention-${data.type}`);
    node.innerText = data.value;
    return node;
  }

  static value(node) {
    return {
      id: node.getAttribute('data-id'),
      type: node.getAttribute('data-type'),
      value: node.getAttribute('data-value') || node.innerText
    };
  }

  // Required for Embed blots
  static formats(node) {
    return {
      id: node.getAttribute('data-id'),
      type: node.getAttribute('data-type'),
      value: node.getAttribute('data-value') || node.innerText
    };
  }
}

class MentionLinkBlot extends Embed {
  static blotName = 'mention-link';
  static tagName = 'a';
  static className = 'ql-mention-link';

  static create(data) {
    const node = super.create();
    node.setAttribute('href', `/client/candidate/${data.id}`);
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
    node.setAttribute('data-id', data.id);
    node.setAttribute('data-type', 'candidate');
    node.setAttribute('data-value', data.value);
    node.setAttribute('contenteditable', 'false');
    node.classList.add('mention');
    node.classList.add('mention-candidate');
    node.innerText = data.value;
    return node;
  }

  static value(node) {
    return {
      id: node.getAttribute('data-id'),
      type: 'candidate',
      value: node.getAttribute('data-value') || node.innerText
    };
  }

  static formats(node) {
    return {
      id: node.getAttribute('data-id'),
      type: 'candidate',
      value: node.getAttribute('data-value') || node.innerText
    };
  }
}

// Register the blots ONCE
Quill.register(MentionBlot, true);
Quill.register(MentionLinkBlot, true);

// ============================================================================
// NOTES PANEL COMPONENT
// ============================================================================

const NotesPanel = ({ 
  isOpen, 
  onClose, 
  candidateId, 
  candidateName,
  firmId,
  currentUser,
  embedded
}) => {
  // Main state
  const [activeTab, setActiveTab] = useState('all');
  const [notes, setNotes] = useState([]);
  const [mentions, setMentions] = useState([]);
  // Profile-changes feed — same data the old ActivityWidget pulled from
  // /api/candidates/:id/activity. Lives inside this panel as its own tab
  // so all candidate-related streams (notes, mentions, profile changes)
  // are reachable from a single sidebar instead of multiple icons.
  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editorHeight, setEditorHeight] = useState(120);
  // Draft autosave — persists the in-progress note to localStorage on
  // every keystroke (debounced) so a refresh, browser crash, or network
  // blip doesn't lose work. `draftSavedAt` drives the small "Saved
  // [time]" indicator; `draftRecovered` lights up briefly when an
  // unsubmitted draft is loaded so the user knows where the text came
  // from. The key includes candidateId AND editingNoteId so drafts for
  // editing existing notes don't collide with drafts for new notes on
  // the same candidate.
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [draftRecovered, setDraftRecovered] = useState(false);
  const draftSaveTimerRef = useRef(null);
  const draftKey = useMemo(() => {
    if (!candidateId) return null;
    return `note_draft:${candidateId}:${editingNoteId || 'new'}`;
  }, [candidateId, editingNoteId]);

  // Toolbar visibility: Quill's built-in toolbar IS shown — bold,
  // italic, underline, lists, link, etc. — so users can format notes
  // without remembering keyboard shortcuts. The hide-on-mount effect
  // that used to live here was removed. Toolbar height is measured at
  // sync time and subtracted from the editor container so heights
  // still add up to the wrapper.

  // Sync Quill's internal heights to the wrapper. The CSS chain
  // (wrapper height → flex column → flex:1 ql-container → height:100% ql-editor)
  // looked right but in practice the editable area stopped growing past a
  // certain point — Quill's bundled stylesheet sets `.ql-container { height: 100% }`
  // which conflicts with my `flex: 1 1 auto`, and `min-height: 0` in flex children
  // doesn't always cascade through ReactQuill's wrapper. Setting heights
  // imperatively here is a no-cascade guarantee: whatever the wrapper is,
  // the editable area matches it. Runs on every editorHeight change, after
  // showEditor toggles on, and after the panel measures itself for the
  // first time. The 16px subtraction accounts for the outer p-2 padding.
  useEffect(() => {
    if (!showEditor) return;
    const apply = () => {
      const editor = quillRef.current?.getEditor && quillRef.current.getEditor();
      if (!editor) return;
      const root = editor.root;                     // .ql-editor
      const container = root?.parentElement;        // .ql-container
      const outer = container?.parentElement;       // ReactQuill's outer div
      // Measure the toolbar's natural height (varies by font + line-
      // wrapping if too many buttons fit). Subtract it so the editable
      // area ends up exactly editorHeight - 16 - toolbarHeight, and the
      // toolbar + editor together fill the wrapper without overflow.
      const toolbar = outer?.querySelector('.ql-toolbar');
      const toolbarH = toolbar ? toolbar.getBoundingClientRect().height : 0;
      const innerH = Math.max(0, editorHeight - 16 - toolbarH);
      if (outer) {
        outer.style.height = `${editorHeight - 16}px`;
        outer.style.display = 'flex';
        outer.style.flexDirection = 'column';
      }
      if (container) {
        container.style.height = `${innerH}px`;
        container.style.minHeight = `${innerH}px`;
        container.style.maxHeight = `${innerH}px`;
        container.style.boxSizing = 'border-box';
      }
      if (root) {
        root.style.height = `${innerH}px`;
        root.style.minHeight = `${innerH}px`;
        root.style.maxHeight = `${innerH}px`;
        root.style.overflowY = 'auto';
        root.style.boxSizing = 'border-box';
      }
    };
    apply();
    // ReactQuill occasionally finishes mutating its DOM on the microtask
    // after our effect — re-apply to win that race.
    const t = setTimeout(apply, 0);
    return () => clearTimeout(t);
  }, [editorHeight, showEditor]);

  // Track the body container's height so the editor's resize maxes out at
  // 90% of the available panel height — not a fixed px value. This lets
  // the same component feel right on a 13" laptop AND a 4K monitor: drag
  // the handle as far as you like, and the cap moves with the viewport.
  // The remaining 10% headroom leaves space for the resize handle and
  // action buttons row below the editor.
  useEffect(() => {
    const node = bodyRef.current;
    if (!node) return;
    const update = () => {
      const h = node.clientHeight;
      if (h > 0) {
        const max = Math.max(120, Math.floor(h * 0.9));
        setMaxEditorHeight(max);
        // Clamp current value if it now exceeds the new max (e.g. user
        // shrank the browser window after dragging the editor large).
        setEditorHeight(prev => Math.min(prev, max));
      }
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(node);
    return () => ro.disconnect();
  }, [isOpen]);
  const [width, setWidth] = useState(600);
  const [isResizing, setIsResizing] = useState(false);
  
  // Mention autocomplete state
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionType, setMentionType] = useState(null);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const [mentionCursorPosition, setMentionCursorPosition] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  
  // Refs
  const panelRef = useRef(null);
  const quillRef = useRef(null);
  const dropdownRef = useRef(null);
  // Ref to the body container — used to compute a dynamic max for the
  // editor's resize so it can grow to 90% of the actual panel height
  // (instead of being capped at a fixed pixel value that's wrong for a
  // 4K monitor and too generous for a 13" laptop).
  const bodyRef = useRef(null);
  // Dynamic cap on the editor's draggable height. Updated by a
  // ResizeObserver below as the panel changes size. Until the observer
  // measures, fall back to 1200 so the resize feels normal on first paint.
  const [maxEditorHeight, setMaxEditorHeight] = useState(1200);
  const isInsertingMention = useRef(false);
  const editorInitialized = useRef(false);

  const MIN_WIDTH = 400;
  const MAX_WIDTH_PERCENT = 0.8;

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Load data when panel opens
  useEffect(() => {
    if (isOpen && candidateId) {
      loadNotes();
      loadMentions();
    }
  }, [isOpen, candidateId]);

  // Auto-refresh when the AI assistant adds a note to THIS candidate.
  // The launcher dispatches a window CustomEvent on every successful
  // write tool_result; we filter by tool + candidate_id to decide
  // whether the update is relevant to this panel instance. The check
  // is cheap (boolean equality), and the listener is no-op for any
  // unrelated tool, so leaving it always-on (not just when isOpen) is
  // fine — the user might add a note while the panel is closed and
  // expect to see it when they reopen.
  useEffect(() => {
    if (!candidateId) return;
    const onAssistantWrite = (e) => {
      const d = e.detail || {};
      if (d.tool === 'add_note' && Number(d.input?.candidate_id) === Number(candidateId)) {
        loadNotes();
      }
    };
    window.addEventListener('banyak:assistant_write', onAssistantWrite);
    return () => window.removeEventListener('banyak:assistant_write', onAssistantWrite);
  }, [candidateId]);

  // Lazy-load the profile activity stream the first time the recruiter
  // opens the "Profile Changes" tab in this session, and refresh it on
  // subsequent activations so the feed stays current. Cheaper than
  // requesting on every panel open (most users never open this tab).
  useEffect(() => {
    if (isOpen && candidateId && activeTab === 'activity') {
      loadActivity();
    }
    // Deps intentionally limited to (isOpen, candidateId, activeTab):
    // loadActivity is defined in this component and doesn't need to be
    // re-fetched when its identity changes between renders. The exhaustive
    // -deps rule isn't enabled in this project, so we can't disable it
    // by name without breaking the build.
  }, [isOpen, candidateId, activeTab]);

  // Handle resize dragging
  useEffect(() => {
    if (isResizing) {
      const handleMove = (e) => {
        const newWidth = window.innerWidth - e.clientX;
        const maxWidth = window.innerWidth * MAX_WIDTH_PERCENT;
        if (newWidth >= MIN_WIDTH && newWidth <= maxWidth) {
          setWidth(newWidth);
        }
      };
      
      const handleUp = () => setIsResizing(false);
      
      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleUp);
      return () => {
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleUp);
      };
    }
  }, [isResizing]);

  // Search mentions when search term changes
  useEffect(() => {
    if (mentionType && showMentionDropdown) {
      const timer = setTimeout(() => {
        searchMentions(mentionSearch, mentionType);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [mentionSearch, mentionType, showMentionDropdown]);

  // Style mentions in saved notes (DOM manipulation for display)
  useEffect(() => {
    if (notes.length > 0) {
      setTimeout(styleMentionsInDOM, 50);
    }
  }, [notes]);

  useEffect(() => {
    if (mentions.length > 0 && activeTab === 'mentions') {
      setTimeout(styleMentionsInDOM, 50);
    }
  }, [mentions, activeTab]);

  // Initialize Quill editor with keyboard bindings
  useEffect(() => {
    if (!isOpen || editorInitialized.current) return;
    
    const timer = setTimeout(() => {
      const quill = quillRef.current?.getEditor();
      if (!quill) return;
      
      editorInitialized.current = true;
      
      // Get keyboard module
      const keyboard = quill.getModule('keyboard');
      if (!keyboard) return;
      
      // Helper function to check if a blot is a mention
      const isMentionBlot = (blot) => {
        if (!blot || !blot.statics) return false;
        const blotName = blot.statics.blotName;
        return blotName === 'mention' || blotName === 'mention-link';
      };

      // Add backspace binding using addBinding (safer approach)
      keyboard.addBinding(
        { key: 'Backspace' },
        function(range) {
          if (range.index > 0 && range.length === 0) {
            const [blot] = quill.getLeaf(range.index - 1);
            if (isMentionBlot(blot)) {
              quill.deleteText(range.index - 1, 1, Quill.sources.USER);
              return false;
            }
          }
          return true;
        }
      );
      
      // Add delete key binding
      keyboard.addBinding(
        { key: 'Delete' },
        function(range) {
          if (range.length === 0) {
            const [blot] = quill.getLeaf(range.index);
            if (isMentionBlot(blot)) {
              quill.deleteText(range.index, 1, Quill.sources.USER);
              return false;
            }
          }
          return true;
        }
      );
    }, 200);

    return () => clearTimeout(timer);
  }, [isOpen]);

  // Reset editor state when panel closes
  useEffect(() => {
    if (!isOpen) {
      editorInitialized.current = false;
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showMentionDropdown && dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        closeMentionDropdown();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMentionDropdown]);

  // ============================================================================
  // API FUNCTIONS
  // ============================================================================

  const loadNotes = async () => {
    try {
      const response = await fetch(`/api/client/candidates/${candidateId}/notes/all`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setNotes(data || []);
      }
    } catch (error) {
      console.error('Error loading notes:', error);
    }
  };

  const loadMentions = async () => {
    try {
      const response = await fetch(`/api/client/candidates/${candidateId}/notes/mentions`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMentions(data || []);
      }
    } catch (error) {
      console.error('Error loading mentions:', error);
    }
  };

  // Fetch the candidate's profile activity stream (same endpoint the old
  // ActivityWidget used). Called lazily — only when the "Profile Changes"
  // tab is activated — to avoid an extra request on every panel open.
  const loadActivity = async () => {
    setActivityLoading(true);
    try {
      const response = await fetch(`/api/candidates/${candidateId}/activity?limit=50`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setActivity(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error loading activity:', error);
    } finally {
      setActivityLoading(false);
    }
  };

  const searchMentions = async (query, type) => {
    try {
      // For @user mentions we want users in the LOGGED-IN recruiter's firm
      // (their colleagues), not necessarily the candidate's firm. The
      // `firmId` prop is the candidate's client_data.firm_id, which is only
      // correct when the recruiter is at that same firm. Prefer currentUser.firm_id
      // when available and fall back to the prop for backwards compatibility.
      const effectiveFirmId = currentUser?.firm_id || firmId || '';
      const url = type === 'user'
        ? `/api/users/search?q=${encodeURIComponent(query || '')}&firm_id=${effectiveFirmId}`
        : `/api/candidates/mentions?q=${encodeURIComponent(query || '')}`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Backend may return either an array or { candidates: [...] }
        const list = Array.isArray(data) ? data : (data.candidates || []);
        setMentionSuggestions(list);
      } else {
        setMentionSuggestions([]);
      }
    } catch (error) {
      console.error('[NotesPanel] searchMentions error:', error);
      setMentionSuggestions([]);
    }
  };

  // ============================================================================
  // DOM STYLING (for saved notes display)
  // This only styles plain text mentions that weren't created with Quill blots
  // ============================================================================

  const styleMentionsInDOM = () => {
    // Style candidate links that don't already have mention class
    const candidateLinks = document.querySelectorAll('.prose a[href^="/client/candidate/"]');
    candidateLinks.forEach(link => {
      if (!link.classList.contains('mention')) {
        link.classList.add('mention');
        link.classList.add('mention-candidate');
      }
    });

    // Helper to check if an element or any ancestor has mention styling
    const isInsideMention = (element) => {
      let current = element;
      while (current && current !== document.body) {
        if (current.classList && (
          current.classList.contains('mention') ||
          current.classList.contains('ql-mention') ||
          current.classList.contains('ql-mention-link') ||
          current.hasAttribute?.('data-id') ||
          current.hasAttribute?.('data-type')
        )) {
          return true;
        }
        current = current.parentElement;
      }
      return false;
    };

    // Style @mentions in prose containers - ONLY for plain text mentions
    const proseContainers = document.querySelectorAll('.prose');
    proseContainers.forEach(container => {
      const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
      let node;
      const nodesToProcess = [];
      
      while ((node = walker.nextNode())) {
        const text = node.textContent;
        // Only process if:
        // 1. Has @ followed by capital letter
        // 2. Parent is NOT already a mention element
        // 3. Not inside any mention ancestor
        if (/@[A-Z]/.test(text) && !isInsideMention(node.parentElement)) {
          nodesToProcess.push(node);
        }
      }

      nodesToProcess.forEach(textNode => {
        const text = textNode.textContent;
        // Match @Name or @First Last (capitalized names only)
        const mentionPattern = /@([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/g;
        
        if (mentionPattern.test(text)) {
          mentionPattern.lastIndex = 0; // Reset regex
          const fragment = document.createDocumentFragment();
          let lastIndex = 0;
          let match;
          
          while ((match = mentionPattern.exec(text)) !== null) {
            // Add text before match
            if (match.index > lastIndex) {
              fragment.appendChild(document.createTextNode(text.substring(lastIndex, match.index)));
            }
            
            // Create styled mention span
            const span = document.createElement('span');
            span.className = 'mention mention-user';
            span.textContent = match[0];
            fragment.appendChild(span);
            
            lastIndex = match.index + match[0].length;
          }
          
          // Add remaining text
          if (lastIndex < text.length) {
            fragment.appendChild(document.createTextNode(text.substring(lastIndex)));
          }
          
          textNode.parentNode.replaceChild(fragment, textNode);
        }
      });
    });
  };

  // ============================================================================
  // MENTION DROPDOWN HELPERS
  // ============================================================================

  const closeMentionDropdown = useCallback(() => {
    setShowMentionDropdown(false);
    setMentionType(null);
    setMentionSearch('');
    setMentionSuggestions([]);
    setMentionCursorPosition(null);
    setSelectedSuggestionIndex(0);
  }, []);

  const updateDropdownPosition = useCallback((quill, cursorPos) => {
    try {
      const bounds = quill.getBounds(cursorPos);
      const editorRect = quill.root.getBoundingClientRect();
      // Position one line BELOW the cursor (matching the LinkedIn
      // extension's mention popover). bounds.top is the top of the
      // current line within the editor, bounds.height is its line height,
      // so adding both lands the popover just below the line being typed.
      // The 4px nudge gives a small visual gap.
      setDropdownPosition({
        top: editorRect.top + bounds.top + bounds.height + 4,
        left: Math.min(editorRect.left + bounds.left, window.innerWidth - 240)
      });
    } catch (e) {
      // Ignore bounds errors
    }
  }, []);

  // ============================================================================
  // TEXT CHANGE HANDLER - Detects @ and # triggers
  // ============================================================================

  const handleTextChange = useCallback((content, delta, source, editor) => {
    setNoteContent(content);
    
    // Ignore programmatic changes and changes during mention insertion
    if (source !== 'user' || isInsertingMention.current) {
      return;
    }

    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const selection = quill.getSelection();
    if (!selection) {
      closeMentionDropdown();
      return;
    }

    const cursorPosition = selection.index;
    const text = quill.getText(0, cursorPosition);
    
    // Check for @ or # trigger
    const atMatch = text.match(/@([^@#\n]*)$/);
    const hashMatch = text.match(/#([^@#\n]*)$/);

    if (atMatch) {
      const searchTerm = atMatch[1];
      updateDropdownPosition(quill, cursorPosition);
      
      setMentionType('user');
      setMentionSearch(searchTerm);
      setMentionCursorPosition(cursorPosition - searchTerm.length - 1);
      setShowMentionDropdown(true);
      setSelectedSuggestionIndex(0);
      // Fire search IMMEDIATELY for the trigger keystroke (don't wait for
      // the debounced effect). This is what makes the dropdown populate
      // the instant `@` is pressed, instead of feeling unresponsive
      // because of the 100ms debounce on the first character.
      searchMentions(searchTerm, 'user');
    } else if (hashMatch) {
      const searchTerm = hashMatch[1];
      updateDropdownPosition(quill, cursorPosition);
      
      setMentionType('candidate');
      setMentionSearch(searchTerm);
      setMentionCursorPosition(cursorPosition - searchTerm.length - 1);
      setShowMentionDropdown(true);
      setSelectedSuggestionIndex(0);
      searchMentions(searchTerm, 'candidate');
    } else {
      closeMentionDropdown();
    }
  }, [closeMentionDropdown, updateDropdownPosition]);

  // ============================================================================
  // INSERT MENTION - Uses Embed blots
  // ============================================================================

  const insertMention = useCallback((item) => {
    const quill = quillRef.current?.getEditor();
    if (!quill || mentionCursorPosition === null) return;

    isInsertingMention.current = true;

    try {
      // Calculate how much text to delete (@ or # plus search term)
      const deleteLength = mentionSearch.length + 1;
      
      // Delete the trigger text (@search or #search)
      quill.deleteText(mentionCursorPosition, deleteLength, Quill.sources.SILENT);
      
      // Create mention text with @ or # prefix
      const mentionText = mentionType === 'user' ? `@${item.name}` : `#${item.name}`;
      
      // Insert the appropriate embed blot
      if (mentionType === 'user') {
        quill.insertEmbed(mentionCursorPosition, 'mention', {
          id: String(item.id),
          type: 'user',
          value: mentionText
        }, Quill.sources.USER);
      } else {
        quill.insertEmbed(mentionCursorPosition, 'mention-link', {
          id: String(item.id),
          type: 'candidate',
          value: mentionText
        }, Quill.sources.USER);
      }
      
      // Insert a space after the mention
      const newPosition = mentionCursorPosition + 1;
      quill.insertText(newPosition, ' ', Quill.sources.USER);
      
      // Set cursor after the space
      setTimeout(() => {
        const editor = quillRef.current?.getEditor();
        if (editor) {
          editor.setSelection(newPosition + 1, 0, Quill.sources.SILENT);
          editor.focus();
        }
        isInsertingMention.current = false;
      }, 10);

    } catch (error) {
      console.error('Error inserting mention:', error);
      isInsertingMention.current = false;
    }

    // Close dropdown
    closeMentionDropdown();
  }, [mentionType, mentionSearch, mentionCursorPosition, closeMentionDropdown]);

  // ============================================================================
  // KEYBOARD NAVIGATION FOR DROPDOWN
  // ============================================================================

  const handleKeyDown = useCallback((e) => {
    if (!showMentionDropdown || mentionSuggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        e.stopPropagation();
        setSelectedSuggestionIndex(prev => 
          prev < mentionSuggestions.length - 1 ? prev + 1 : 0
        );
        break;
        
      case 'ArrowUp':
        e.preventDefault();
        e.stopPropagation();
        setSelectedSuggestionIndex(prev => 
          prev > 0 ? prev - 1 : mentionSuggestions.length - 1
        );
        break;
        
      case 'Enter':
        e.preventDefault();
        e.stopPropagation();
        if (mentionSuggestions[selectedSuggestionIndex]) {
          insertMention(mentionSuggestions[selectedSuggestionIndex]);
        }
        break;
        
      case 'Tab':
        e.preventDefault();
        e.stopPropagation();
        if (mentionSuggestions[selectedSuggestionIndex]) {
          insertMention(mentionSuggestions[selectedSuggestionIndex]);
        }
        break;
        
      case 'Escape':
        e.preventDefault();
        e.stopPropagation();
        closeMentionDropdown();
        break;
        
      default:
        break;
    }
  }, [showMentionDropdown, mentionSuggestions, selectedSuggestionIndex, insertMention, closeMentionDropdown]);

  // ============================================================================
  // DRAFT AUTOSAVE
  // ============================================================================

  /**
   * Has-draft detection. Keeps an "is there a recoverable draft for
   * the current candidate?" flag independent of whether the editor is
   * open, so we can show a banner inviting the user to resume even
   * when the editor isn't visible. Recomputed when the candidate
   * changes or when we save/clear a draft.
   */
  const [hasDraft, setHasDraft] = useState(false);

  // Refs holding latest noteContent + draftKey + editingNoteId so the
  // unmount cleanup below sees the values at unmount time (not the
  // values captured when the effect was set up). Without these refs,
  // an unmount that happens mid-typing would save STALE content.
  const latestContentRef = useRef('');
  const latestDraftKeyRef = useRef(null);
  const latestEditingIdRef = useRef(null);
  useEffect(() => { latestContentRef.current = noteContent; }, [noteContent]);
  useEffect(() => { latestDraftKeyRef.current = draftKey; }, [draftKey]);
  useEffect(() => { latestEditingIdRef.current = editingNoteId; }, [editingNoteId]);

  // Unmount handler — fires when the component is removed from the
  // React tree (e.g. SPA navigation away from the candidate). This
  // is the ONLY save path that runs without a beforeunload event, so
  // it's critical for in-app navigation that doesn't reload the page.
  useEffect(() => {
    return () => {
      const content = latestContentRef.current;
      const key = latestDraftKeyRef.current;
      const eid = latestEditingIdRef.current;
      if (!key || !content || content === '<p><br></p>') return;
      try {
        localStorage.setItem(key, JSON.stringify({
          content,
          savedAt: Date.now(),
          editingNoteId: eid || null,
        }));
      } catch {}
    };
  }, []);

  const recomputeHasDraft = useCallback(() => {
    if (!draftKey) { setHasDraft(false); return; }
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) { setHasDraft(false); return; }
      const draft = JSON.parse(raw);
      setHasDraft(!!(draft?.content && draft.content !== '<p><br></p>'));
    } catch { setHasDraft(false); }
  }, [draftKey]);

  /**
   * Persist the in-progress note to localStorage on every keystroke.
   * CRITICAL: this effect ONLY saves content. It never wipes the
   * stored draft based on empty content — that was the bug that
   * killed all autosave: on initial mount with noteContent='',
   * this effect was removing any previously-saved draft BEFORE the
   * recovery effect could load it. Now, drafts are only removed by
   * explicit user action (successful Submit, explicit Discard).
   * If the user empties the editor by deleting all text, the last
   * non-empty content stays in localStorage as a safety net — they
   * can always Discard if they really want it gone.
   *
   * No debounce. localStorage writes are fast enough (~0.1ms in
   * Chrome) that writing on every keystroke is imperceptible, and
   * removing the debounce eliminates the entire data-loss window
   * between last-keystroke and the next 250ms.
   */
  useEffect(() => {
    if (!draftKey) return;
    if (!noteContent || noteContent === '<p><br></p>') return; // skip, don't wipe
    try {
      localStorage.setItem(draftKey, JSON.stringify({
        content: noteContent,
        savedAt: Date.now(),
        editingNoteId: editingNoteId || null,
      }));
      setDraftSavedAt(Date.now());
      setHasDraft(true);
    } catch (e) {
      // Quota exceeded or storage unavailable — fail silently. The
      // user still sees their note in the editor; we just can't
      // recover it across reloads.
    }
  }, [noteContent, draftKey, editingNoteId]);

  /**
   * Synchronous save on page unload (close tab, reload, navigate
   * away). The 250ms debounce above means a fast typist could
   * navigate away mid-debounce and lose the most recent keystroke.
   * This listener fires immediately and writes the current content,
   * bypassing the timer. Synchronous localStorage.setItem is safe
   * here — modern browsers allow it during beforeunload.
   *
   * Same logic also runs on visibility change (tab backgrounded) and
   * on pagehide (catches mobile background + bfcache cases that
   * beforeunload misses).
   */
  useEffect(() => {
    if (!draftKey) return;
    const saveNow = () => {
      if (!noteContent || noteContent === '<p><br></p>') return;
      try {
        if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current);
        localStorage.setItem(draftKey, JSON.stringify({
          content: noteContent,
          savedAt: Date.now(),
          editingNoteId: editingNoteId || null,
        }));
      } catch {}
    };
    const onVisibility = () => { if (document.visibilityState === 'hidden') saveNow(); };
    window.addEventListener('beforeunload', saveNow);
    window.addEventListener('pagehide', saveNow);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('beforeunload', saveNow);
      window.removeEventListener('pagehide', saveNow);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [draftKey, noteContent, editingNoteId]);

  /**
   * Candidate switch handler. When the candidate prop changes, the
   * current noteContent belongs to the PREVIOUS candidate's draft
   * key — flush it synchronously to localStorage before the
   * draftKey recomputes, then clear noteContent so the new
   * candidate doesn't inherit text from the previous one. Recovery
   * happens via the effect below once the new draftKey takes effect.
   *
   * Tracks the previous candidateId via a ref so we can detect the
   * change (props don't expose "previous" directly).
   */
  const prevCandidateIdRef = useRef(candidateId);
  useEffect(() => {
    const prev = prevCandidateIdRef.current;
    if (prev === candidateId) return;
    // Save the outgoing candidate's draft synchronously before
    // anything else changes.
    if (prev && noteContent && noteContent !== '<p><br></p>') {
      try {
        const prevKey = `note_draft:${prev}:${editingNoteId || 'new'}`;
        localStorage.setItem(prevKey, JSON.stringify({
          content: noteContent,
          savedAt: Date.now(),
          editingNoteId: editingNoteId || null,
        }));
      } catch {}
    }
    // Reset editor state for the new candidate. The recovery effect
    // below will repopulate from localStorage if a draft exists.
    setNoteContent('');
    setEditingNoteId(null);
    setShowEditor(false);
    setDraftSavedAt(null);
    setDraftRecovered(false);
    prevCandidateIdRef.current = candidateId;
  }, [candidateId]);

  /**
   * On editor open OR candidate change, check for a stored draft and
   * restore it. Skips when the editor already has content (e.g.
   * handleEdit just populated it for editing an existing note).
   *
   * Recovery is silent + automatic — the user just sees their text
   * reappear along with a "Draft recovered" badge in the footer.
   */
  useEffect(() => {
    if (!draftKey) return;
    if (!showEditor) {
      // Just update the has-draft flag so the banner appears even
      // when the editor is closed.
      recomputeHasDraft();
      return;
    }
    if (noteContent && noteContent !== '<p><br></p>') return; // already has content
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (draft?.content && draft.content !== '<p><br></p>') {
        setNoteContent(draft.content);
        setDraftRecovered(true);
        setHasDraft(true);
        setTimeout(() => setDraftRecovered(false), 6000);
      }
    } catch (e) { /* ignore corrupt draft */ }
    // Deps: showEditor + draftKey only (NOT noteContent — that's the
    // autosave's job, not recovery's). exhaustive-deps rule not
    // configured so no eslint disable comment needed.
  }, [showEditor, draftKey, recomputeHasDraft]);

  /**
   * Resume from draft banner action. Loads the draft into the editor
   * and opens it. Used when the editor was closed but a draft exists.
   */
  const resumeDraft = useCallback(() => {
    if (!draftKey) return;
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (draft?.content) {
        if (draft.editingNoteId) setEditingNoteId(draft.editingNoteId);
        setNoteContent(draft.content);
        setShowEditor(true);
        setDraftRecovered(true);
        setTimeout(() => setDraftRecovered(false), 6000);
      }
    } catch {}
  }, [draftKey]);

  /**
   * Clear any persisted draft for the current key. Called ONLY from
   * handleSubmit success and the explicit Discard-draft action —
   * NOT from Cancel (closing the editor should preserve the draft
   * so the user can come back).
   */
  const clearDraft = useCallback(() => {
    if (!draftKey) return;
    try { localStorage.removeItem(draftKey); } catch {}
    setDraftSavedAt(null);
    setDraftRecovered(false);
    setHasDraft(false);
  }, [draftKey]);

  // ============================================================================
  // FORM HANDLERS
  // ============================================================================

  const handleSubmit = async () => {
    if (!noteContent.trim() || noteContent === '<p><br></p>') return;

    try {
      setIsSubmitting(true);
      
      const endpoint = editingNoteId 
        ? `/api/client/candidates/${candidateId}/notes/${editingNoteId}`
        : `/api/client/candidates/${candidateId}/notes/all`;
      
      const method = editingNoteId ? 'PUT' : 'POST';

      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ content: noteContent })
      });

      if (response.ok) {
        clearDraft();
        setNoteContent('');
        setEditingNoteId(null);
        setShowEditor(false);
        loadNotes();
        loadMentions();
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      console.error('Error saving note:', error);
      alert('Failed to save note');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (note) => {
    setNoteContent(note.content);
    setEditingNoteId(note.id);
    setShowEditor(true);
    setActiveTab('notes');
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;

    try {
      const response = await fetch(`/api/client/candidates/${candidateId}/notes/${noteId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });

      if (response.ok) {
        loadNotes();
        loadMentions();
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      alert('Failed to delete note');
    }
  };

  const handleCancel = () => {
    // Cancel CLOSES the editor but preserves both noteContent and the
    // localStorage draft — the user is just stepping away, not
    // discarding. Reopening the editor brings the text back. Reload
    // / candidate switch / tab close are also covered: noteContent
    // is in state, the draft is in localStorage, recovery runs on
    // next open.
    setShowEditor(false);
  };

  const handleDiscardDraft = () => {
    // Explicit user action — wipe the draft and reset state.
    clearDraft();
    setNoteContent('');
    setEditingNoteId(null);
    setShowEditor(false);
  };

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  // ============================================================================
  // QUILL MODULES CONFIG
  // ============================================================================

  const modules = {
    // Quill toolbar layout. Each inner array is a button group; gaps
    // between groups visually separate concerns (text format vs lists
    // vs links/code). Headers ship as a single picker so they don't
    // crowd the toolbar — H1/H2/H3 + normal. Blockquote + code-block
    // are useful for notes that quote candidate replies or capture
    // technical follow-ups.
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['blockquote', 'code-block'],
      ['link'],
      ['clean']
    ],
    keyboard: {
      bindings: {
        // Custom bindings are added in useEffect
      }
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  if (!isOpen) return null;

  // Categorize notes — needed by both rendering modes.
  const isCallNote = (n) => n.content?.includes('📞 Phone Call');
  const isOutreachNote = (n) => !isCallNote(n) && (n.content?.includes('📧 Email') || n.content?.includes('📱 SMS') || n.content?.includes('💬 WhatsApp') || n.content?.includes('✅ Manual Task'));
  const regularNotes = notes.filter(n => !isCallNote(n) && !isOutreachNote(n));
  const callNotes = notes.filter(isCallNote);
  const outreachNotes = notes.filter(isOutreachNote);

  const tabs = [
    { id: 'all', label: 'All', count: notes.length },
    { id: 'notes', label: 'Candidate Notes', count: regularNotes.length },
    { id: 'transcripts', label: 'Transcripts', count: callNotes.length },
    { id: 'outreach', label: 'Outreach', count: outreachNotes.length },
    { id: 'mentions', label: 'Mentions', count: mentions.length },
    // Profile changes — consolidates what used to be the standalone
    // ActivityWidget into this panel so all candidate-related streams live
    // in one place. The tab doesn't show a count because the activity feed
    // is open-ended and a number isn't meaningful for "how much happened".
    { id: 'activity', label: 'Profile Changes' },
  ];

  const activeNotes = activeTab === 'all' ? notes : activeTab === 'notes' ? regularNotes : activeTab === 'transcripts' ? callNotes : activeTab === 'outreach' ? outreachNotes : [];

  // Shared body — same render for embedded + non-embedded modes. This is
  // important because the legacy non-embedded JSX referenced handlers like
  // `handleSaveNote`, `handleMentionUser`, etc. that don't exist in this
  // component (correct names are handleSubmit/handleEdit/etc), and lacked
  // a resize handle entirely. Using one render path means both modes
  // share the same working handlers, the same resize handle, and the
  // same mention dropdown.
  const body = (
      <>
        {/* Scoped fixes for ReactQuill so the editor fills its parent and scrolls
            instead of overflowing the action-button row below it.
            Uses !important and direct-child selectors to win against Quill's own CSS. */}
        <style>{`
          /* Heights for .ql-container and .ql-editor are set imperatively in
             the resize-sync effect — the rules here only handle the look
             (borders, padding, font, scroll). Keeping height rules out of
             CSS avoids cascade fights with Quill's bundled stylesheet. */
          .quill-flex-editor .ql-toolbar {
            border: 1px solid #e5e7eb !important;
            border-bottom: none !important;
            border-radius: 4px 4px 0 0 !important;
            padding: 4px 6px !important;
            background: #f9fafb !important;
          }
          /* Tighten the toolbar so buttons fit comfortably without
             wrapping at narrow widths. Quill's default buttons are
             28x24 which feels chunky inside a side panel. */
          .quill-flex-editor .ql-toolbar button {
            width: 24px !important;
            height: 22px !important;
            padding: 2px 3px !important;
          }
          .quill-flex-editor .ql-toolbar .ql-formats {
            margin-right: 8px !important;
          }
          .quill-flex-editor .ql-container {
            border: 1px solid #e5e7eb !important;
            border-radius: 0 0 4px 4px !important;
            font-size: 13px !important;
            font-family: inherit !important;
            overflow: hidden !important;
          }
          .quill-flex-editor .ql-editor {
            padding: 8px 10px !important;
            font-size: 13px !important;
            line-height: 1.45 !important;
          }
          .quill-flex-editor .ql-editor.ql-blank::before {
            font-size: 13px !important;
            color: #9ca3af !important;
            font-style: normal !important;
            left: 10px !important;
            right: 10px !important;
          }
        `}</style>
        <div ref={bodyRef} className="flex-1 flex flex-col overflow-hidden bg-white h-full">
          {/* Header with new note button + close */}
          <div className="flex-shrink-0 bg-white border-b px-3 h-9 flex items-center justify-between overflow-hidden">
            <span className="text-xs text-gray-500">Activity</span>
            <div className="flex items-center gap-1">
              {!showEditor && (
                <button onClick={() => { setShowEditor(true); setActiveTab((activeTab === 'mentions' || activeTab === 'activity') ? 'all' : activeTab); }}
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50 rounded transition-colors">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                  Add Note
                </button>
              )}
              <button onClick={() => {
                  // Save immediately before closing — if the user
                  // hasn't typed for less than the debounce window
                  // this catches that content. With debounce removed
                  // this is belt-and-braces.
                  if (draftKey && noteContent && noteContent !== '<p><br></p>') {
                    try {
                      localStorage.setItem(draftKey, JSON.stringify({
                        content: noteContent,
                        savedAt: Date.now(),
                        editingNoteId: editingNoteId || null,
                      }));
                    } catch {}
                  }
                  onClose();
                }} title="Close Notes" className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          </div>

          {/* Unsaved-draft banner — appears when the editor is closed but
              there's a recoverable draft in localStorage for this
              candidate. Two actions: Resume (opens the editor with the
              draft loaded) or Discard (wipes the draft). Self-dismisses
              once the editor opens (because !showEditor gate). Persists
              across reloads + candidate switches because hasDraft is
              computed from localStorage on mount + draftKey change. */}
          {!showEditor && hasDraft && (
            <div className="flex-shrink-0 px-3 py-2 bg-amber-50 border-b border-amber-200 text-xs flex items-center justify-between gap-2">
              <span className="text-amber-900 flex items-center gap-1.5 min-w-0">
                <svg className="w-3.5 h-3.5 flex-shrink-0 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <span className="truncate">Unsaved draft from earlier</span>
              </span>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={resumeDraft}
                  className="px-2 py-0.5 text-amber-900 font-medium hover:bg-amber-100 rounded">
                  Resume
                </button>
                <button onClick={handleDiscardDraft}
                  className="px-2 py-0.5 text-gray-500 hover:bg-amber-100 rounded">
                  Discard
                </button>
              </div>
            </div>
          )}

          {/* Sticky tabs */}
          <div className="flex-shrink-0 border-b bg-white overflow-x-auto sticky top-0 z-10">
            <div className="flex">
              {tabs.map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} 
                  className={`px-2 py-1.5 text-[10px] font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-gray-400 hover:text-gray-700'
                  }`}>
                  {tab.label}{tab.count > 0 ? ` ${tab.count}` : ''}
                </button>
              ))}
            </div>
          </div>

          {/* Editor — sticky, resizable. Height is driven by the useEffect above
              which sets Quill's internal DOM heights directly. The outer wrapper
              also clips overflow so a stale render can't bleed onto the handle below. */}
          {showEditor && (activeTab !== 'mentions') && (activeTab !== 'activity') && (
            <div className="flex-shrink-0 border-b bg-gray-50 relative">
              {/* Editor pane height is driven entirely by editorHeight on the
                  outer div. Inside, ReactQuill is a flex column (.quill-flex-editor
                  class), so .ql-container takes flex:1 and .ql-editor scrolls
                  internally. No JS height syncing needed → resize is smooth
                  even when dragging rapidly. */}
              <div className="p-2" style={{ height: `${editorHeight}px` }}>
                <div onKeyDown={handleKeyDown} className="h-full">
                  <ReactQuill ref={quillRef} value={noteContent} onChange={handleTextChange} modules={modules}
                    placeholder="Write a note... @ users, # candidates"
                    className="bg-white rounded quill-flex-editor" />
                </div>
              </div>
              {/* Resize handle — explicit z-index so any stray editor overflow can't cover it */}
              <div className="relative z-20 h-1.5 cursor-row-resize bg-gray-100 hover:bg-emerald-200 flex items-center justify-center transition-colors"
                onMouseDown={(e) => {
                  e.preventDefault();
                  const startY = e.clientY;
                  const startH = editorHeight;
                  const onMove = (ev) => setEditorHeight(Math.max(80, Math.min(maxEditorHeight, startH + ev.clientY - startY)));
                  const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
                  document.addEventListener('mousemove', onMove);
                  document.addEventListener('mouseup', onUp);
                }}>
                <div className="w-8 h-0.5 bg-gray-300 rounded"></div>
              </div>
              <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-t border-gray-200 relative z-10">
                <div className="flex items-center gap-2 text-[10px] text-gray-400">
                  <span>@ users · # candidates</span>
                  {/* Draft status indicator. `draftRecovered` flashes
                      green when an unsaved draft from a previous
                      session was loaded; `draftSavedAt` shows after
                      the autosave fires. Mutually exclusive priority:
                      recovered wins because it's a one-time message
                      the user needs to acknowledge, saved is the
                      ambient "your work is safe" feedback. */}
                  {draftRecovered ? (
                    <span className="flex items-center gap-1 text-emerald-600 font-medium">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 10h10a8 8 0 018 8v2M3 10l6 6M3 10l6-6" />
                      </svg>
                      Draft recovered
                    </span>
                  ) : draftSavedAt ? (
                    <span className="flex items-center gap-1 text-gray-400" title={`Saved locally at ${new Date(draftSavedAt).toLocaleTimeString()}`}>
                      <svg className="w-2.5 h-2.5 text-emerald-500" fill="currentColor" viewBox="0 0 8 8">
                        <circle cx="4" cy="4" r="3" />
                      </svg>
                      Draft saved
                    </span>
                  ) : null}
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={handleCancel} className="px-3 py-1 text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors">Cancel</button>
                  {editingNoteId && <button type="button" onClick={handleDiscardDraft} className="px-3 py-1 text-xs border border-gray-300 text-gray-600 rounded hover:bg-gray-50 transition-colors">Discard</button>}
                  <button type="button" onClick={handleSubmit} disabled={isSubmitting || !noteContent.trim() || noteContent === '<p><br></p>'}
                    className="px-3 py-1 bg-emerald-600 text-white text-xs font-medium rounded hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors">
                    {isSubmitting ? '...' : editingNoteId ? 'Update' : 'Add Note'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto">            {activeTab === 'mentions' ? (
              <div className="p-3 space-y-1.5">
                {mentions.length === 0 ? (
                  <div className="text-center py-6"><p className="text-xs text-gray-400">No mentions yet</p></div>
                ) : mentions.map(mention => <MentionCard key={mention.id} mention={mention} formatDate={formatDate} />)}
              </div>
            ) : activeTab === 'activity' ? (
              <ActivityFeed activity={activity} loading={activityLoading} />
            ) : (
              <div className="p-3 space-y-1.5">
                {activeNotes.length === 0 ? (
                  <div className="text-center py-6"><p className="text-xs text-gray-400">No {activeTab === 'notes' ? 'candidate notes' : activeTab === 'transcripts' ? 'call transcripts' : activeTab === 'outreach' ? 'outreach activity' : 'activity'} yet</p></div>
                ) : activeNotes.map(note => (
                  <NoteCard key={note.id} note={note} currentUserId={currentUser?.id} onEdit={handleEdit} onDelete={handleDelete} formatDate={formatDate} />
                ))}
              </div>
            )}
          </div>
        </div>

      {/* Mention Dropdown — portaled to <body> so it can't be trapped by
          ancestor `transform` (the candidate preview panel uses transform
          for its slide-in animation, which creates a containing block for
          position:fixed descendants). Styled to match the LinkedIn
          extension's mention popover: 220px wide, neutral border, no
          translateY — drops below the cursor line instead of above. */}
      {showMentionDropdown && createPortal(
        <div
          ref={dropdownRef}
          className="fixed z-[100] bg-white border border-gray-300 rounded-md shadow-lg overflow-y-auto"
          style={{
            width: '220px',
            maxHeight: '220px',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            padding: '3px'
          }}
        >
          {mentionSuggestions.length === 0 && (
            <div className="py-2 px-2 text-[10px] text-gray-400 text-center italic">
              {mentionSearch
                ? `No ${mentionType === 'user' ? 'users' : 'candidates'} match "${mentionSearch}"`
                : `Type to search ${mentionType === 'user' ? 'users' : 'candidates'}...`}
            </div>
          )}
          {mentionSuggestions.map((item, index) => {
            // Two-line layout. The detail on line 1 differs by type — for
            // users it's their email so you can disambiguate two people
            // with the same name; for candidates it's the title because
            // that's the single most useful signal when scanning the list.
            //   Line 1 (name row):
            //     - candidate: "Name, Title"  (title dropped if absent)
            //     - user:      "Name, Email"  (email dropped if absent)
            //   Line 2 (org row, gray sub-line):
            //     - candidate: current_company
            //     - user:      firm_name (their organization)
            //   Line 2 is omitted entirely when there's nothing to show, so
            //   the option naturally stays compact for sparse records.
            let nameLine = item.name || '';
            let orgLine = '';
            if (mentionType === 'candidate') {
              if (item.current_title) nameLine = `${item.name}, ${item.current_title}`;
              orgLine = item.current_company || '';
            } else {
              if (item.email) nameLine = `${item.name}, ${item.email}`;
              orgLine = item.firm_name || '';
            }
            return (
              <button
                key={item.id}
                onClick={() => insertMention(item)}
                onMouseEnter={() => setSelectedSuggestionIndex(index)}
                className={`w-full text-left px-2 py-1.5 rounded ${
                  index === selectedSuggestionIndex ? 'bg-emerald-50' : 'hover:bg-emerald-50'
                }`}
                title={orgLine ? `${nameLine} — ${orgLine}` : nameLine}
              >
                <div className="text-[11px] font-medium text-gray-800 truncate leading-tight">{nameLine}</div>
                {orgLine && (
                  <div className="text-[9px] text-gray-500 truncate leading-tight mt-0.5">{orgLine}</div>
                )}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </>
  );

  if (embedded) return body;

  // Non-embedded mode: fixed right-side slide-out with a draggable left edge.
  // Inside the panel we just render the same `body` — same tabs, same editor
  // with resize handle, same mention dropdown — so there's only one render
  // path to debug and fix.
  return (
    <div
      ref={panelRef}
      className="fixed top-0 right-0 bottom-0 bg-white shadow-2xl z-50 flex"
      style={{ width: `${width}px` }}
    >
      <div
        className={`w-1 bg-gray-200 hover:bg-emerald-500 cursor-col-resize flex-shrink-0 ${isResizing ? 'bg-emerald-500' : ''}`}
        onMouseDown={handleMouseDown}
      />
      <div className="flex-1 flex flex-col overflow-hidden">
        {body}
      </div>
    </div>
  );
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const NoteCard = ({ note, currentUserId, onEdit, onDelete, formatDate, compact = false }) => {
  // Use == for comparison to handle string/number type differences
  const isOwner = note.created_by == currentUserId;
  // System-generated call notes (with audio/transcript) should not be editable
  const isCallNote = note.content?.includes('📞 Phone Call') && (note.content?.includes('<audio') || note.content?.includes('🎙️ Transcript'));
  
  if (compact) {
    return (
      <div className="bg-white border rounded-lg p-2.5 text-xs">
        <div className="flex items-center gap-1.5 mb-1">
          <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center text-white text-[9px] font-medium flex-shrink-0">
            {note.author_name?.charAt(0) || '?'}
          </div>
          <span className="font-medium text-gray-700 truncate">{note.author_name}</span>
          <span className="text-gray-400 text-[10px]">·</span>
          <span className="text-gray-400 text-[10px] whitespace-nowrap">{formatDate(note.created_at)}</span>
        </div>
        <div className="prose prose-xs max-w-none text-gray-600 line-clamp-3 overflow-hidden" 
          style={{ fontSize: '11px', lineHeight: '1.4' }}
          dangerouslySetInnerHTML={{ __html: note.content }} />
      </div>
    );
  }
  
  return (
    <div className="bg-white border rounded p-2.5 hover:shadow-sm transition-shadow group">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center text-white text-[9px] font-medium flex-shrink-0">
            {note.author_name?.charAt(0) || '?'}
          </div>
          <span className="text-[11px] font-medium text-gray-700 truncate">{note.author_name}</span>
          <span className="text-[10px] text-gray-400 whitespace-nowrap">{formatDate(note.created_at)}</span>
        </div>
        {isOwner && (
          <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
            {!isCallNote && (
            <button onClick={() => onEdit(note)} className="p-1 text-gray-400 hover:text-emerald-700 rounded" title="Edit">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
            </button>
            )}
            <button onClick={() => onDelete(note.id)} className="p-1 text-gray-400 hover:text-red-500 rounded" title="Delete">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
            </button>
          </div>
        )}
      </div>
      <div className="prose prose-xs max-w-none [&_p]:my-0.5 [&_div]:my-0.5" style={{ fontSize: '11px', lineHeight: '1.4' }}
        dangerouslySetInnerHTML={{ __html: note.content }} />
    </div>
  );
};

const MentionCard = ({ mention, formatDate }) => {
  return (
    <div className="bg-emerald-50 border border-emerald-200 rounded p-2.5">
      <div className="flex items-center gap-1.5 mb-1">
        <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center text-white text-[9px] font-medium flex-shrink-0">
          {mention.author_name?.charAt(0) || '?'}
        </div>
        <span className="text-[11px] font-medium text-gray-700 truncate">{mention.author_name}</span>
        <span className="text-[10px] text-gray-500">on <span className="font-medium">{mention.original_candidate_name}</span></span>
        <span className="text-[10px] text-gray-400 whitespace-nowrap">{formatDate(mention.created_at)}</span>
      </div>
      <div className="prose prose-xs max-w-none [&_p]:my-0.5" style={{ fontSize: '11px', lineHeight: '1.4' }}
        dangerouslySetInnerHTML={{ __html: mention.content }} />
    </div>
  );
};

// Profile-changes feed. Same data the ActivityWidget used to display, but
// shown as a tab inside this panel instead of a standalone widget on the
// candidate page. Read-only — recruiters add notes via the editor on the
// other tabs; activity rows are auto-generated by the rest of the system
// (stage moves, doc uploads, tag edits, campaign enrollments, etc.).
const ACTIVITY_ICONS = {
  note_added: '📝',
  card_created: '➕',
  stage_changed: '🔄',
  data_updated: '✏️',
  document_uploaded: '📎',
  rating_changed: '⭐',
  contact_added: '☎️',
  contact_removed: '🚫',
  tag_added: '🏷️',
  tag_removed: '🏷️',
  campaign_enrolled: '📧',
  pipeline_added: '📋',
  pipeline_removed: '📋',
  default: '📌'
};

const formatActionLabel = (action) =>
  action ? action.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase()) : 'Activity';

const formatActivityTime = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const ActivityFeed = ({ activity, loading }) => {
  if (loading) {
    return (
      <div className="p-3 space-y-2">
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />
        ))}
      </div>
    );
  }
  if (!activity || activity.length === 0) {
    return (
      <div className="text-center py-8 px-4">
        <p className="text-xs text-gray-400">No profile changes yet</p>
        <p className="text-[10px] text-gray-300 mt-1">Stage moves, document uploads, contact edits, tag changes — they all show up here.</p>
      </div>
    );
  }
  return (
    <div className="p-3 space-y-0.5">
      {activity.map((act, i) => {
        const action = act.action || act.activity_type;
        const icon = ACTIVITY_ICONS[action] || ACTIVITY_ICONS.default;
        return (
          <div key={act.id || i} className="flex gap-2 px-2 py-2 rounded hover:bg-gray-50">
            <span className="text-sm flex-shrink-0 leading-snug">{icon}</span>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] text-gray-700 leading-snug">
                {act.notes || act.description || formatActionLabel(action)}
              </p>
              <p className="text-[10px] text-gray-400 mt-0.5">
                {act.user_name && <span>{act.user_name}</span>}
                {act.user_name && act.created_at && <span> · </span>}
                {formatActivityTime(act.created_at)}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default NotesPanel;
